import { PORTAL_WEB_PATHS } from '@fos/shared/contracts/portal';
import {
  type AcceptInvitationOutput,
  type InvitationErrorReason,
  type InvitationPreview,
  TEAM_MESSAGES,
  TEAM_WEB_PATHS,
} from '@fos/shared/contracts/team';
import { ROLE_LABELS } from '@fos/shared/labels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, LinkIcon, LogIn, UserPlus } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ApiError, apiFetch, appErrorCode, errorMessage, NetworkError, validationIssues } from '../../../lib/api';
import { fetchSession, SESSION_QUERY_KEY, useLogout, useSession } from '../../../lib/auth';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input, PasswordInput } from '../../../ui/Input';
import { Spinner } from '../../../ui/Spinner';
import { ErrorState } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { AuthLayout } from '../components/AuthLayout';
import { PasswordChecklist } from '../components/PasswordChecklist';
import { passwordChecks, passwordProblem } from '../password-policy';

/**
 * Przyjęcie zaproszenia: /zaproszenie?token=… (link z maila, ważny 7 dni, jednorazowy). Czeka na projekt w Figmie:
 * spokojna karta jak logowanie. Nowe konto: imię i nazwisko, hasło ×2 z polityką → konto, członkostwo, sesja.
 * Konto już istnieje: logowanie, potem „Dołącz” jednym kliknięciem (token czeka w sessionStorage tej karty).
 */

const TOKEN_STORAGE_KEY = 'fos_invitation_token';
const PREVIEW_PATH = '/api/auth/invitation/preview';
const ACCEPT_PATH = '/api/auth/invitation/accept';

function readStoredToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function storeToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    else sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // tryb prywatny bez sessionStorage: token zostaje tylko w stanie historii
  }
}

function invitationReason(err: unknown): InvitationErrorReason | null {
  if (!(err instanceof ApiError)) return null;
  const reason = (err.details as { reason?: unknown } | undefined)?.reason;
  return typeof reason === 'string' ? (reason as InvitationErrorReason) : null;
}

const backLink = (
  <Link
    to="/login"
    className="inline-flex items-center gap-1.5 rounded-xs font-medium text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
  >
    <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
    Przejdź do logowania
  </Link>
);

type FieldErrors = Partial<Record<'name' | 'password' | 'confirm', string>>;

export function InvitationPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const logout = useLogout();

  // Token z adresu przenosimy do stanu historii i sessionStorage (nie zostaje w pasku adresu ani w Referer;
  // po zalogowaniu /login?next=/zaproszenie wraca tutaj bez tokenu w adresie).
  const [token] = useState(
    () => params.get('token')?.trim() || (location.state as { token?: string } | null)?.token || readStoredToken(),
  );
  useEffect(() => {
    if (token) storeToken(token);
    if (params.has('token')) navigate(location.pathname, { replace: true, state: { token } });
  }, [params, navigate, location.pathname, token]);

  const preview = useQuery({
    queryKey: ['invitation', 'preview', token],
    queryFn: () => apiFetch<InvitationPreview>(PREVIEW_PATH, { method: 'POST', body: { token } }),
    enabled: !!token,
    retry: (count, err) => err instanceof NetworkError && count < 2,
    staleTime: 60_000,
  });

  const accept = useMutation({
    mutationFn: (body: { token: string; name?: string; password?: string }) =>
      apiFetch<AcceptInvitationOutput>(ACCEPT_PATH, { method: 'POST', body }),
    onSuccess: async (res) => {
      // zaproszenie do portalu klienta (17.10.2026): od razu lista zgłoszeń (strażnik ról i tak by przekierował);
      // odczyt przed wyczyszczeniem pamięci podręcznej
      const portal = Boolean(
        queryClient.getQueryData<InvitationPreview>(['invitation', 'preview', token])?.portalClientName,
      );
      storeToken(null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_QUERY_KEY[0] });
      await queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 0 });
      toast.success(portal ? 'Witaj w portalu zgłoszeń' : `Witaj w ${res.workspace.name}`);
      navigate(portal ? PORTAL_WEB_PATHS.home : '/dzis', { replace: true });
    },
  });

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  const invitedEmail = preview.data?.email ?? '';
  // portal klienta: imię z zaproszenia jako podpowiedź w formularzu nowego konta (osoba może je poprawić)
  const suggestedName = preview.data?.suggestedName ?? null;
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !suggestedName) return;
    prefilled.current = true;
    setName((current) => current || suggestedName);
  }, [suggestedName]);
  const checks = useMemo(
    () => passwordChecks(password, { email: invitedEmail, name, confirm }),
    [password, invitedEmail, name, confirm],
  );
  const problem = passwordProblem(checks, password);

  const acceptError = accept.error;
  const blockingReason = invitationReason(preview.error) ?? invitationReason(acceptError);
  const terminal = blockingReason === 'invalid' || blockingReason === 'expired' || blockingReason === 'used';

  if (!token || terminal) {
    const message =
      blockingReason === 'expired'
        ? TEAM_MESSAGES.invitationExpired
        : blockingReason === 'used'
          ? TEAM_MESSAGES.invitationUsed
          : TEAM_MESSAGES.invitationInvalid;
    return (
      <AuthLayout title="Zaproszenie nie działa" footer={backLink}>
        <div className="flex flex-col gap-4">
          <span className="inline-flex size-10 items-center justify-center rounded-full bg-surface text-fg-muted">
            <LinkIcon size={20} strokeWidth={1.75} aria-hidden />
          </span>
          <p className="text-body-s text-fg-secondary">{message} Każde zaproszenie działa raz i przez 7 dni.</p>
          {blockingReason === 'used' && (
            <Button asChild variant="primary" size="l" fullWidth>
              <Link to="/login">Zaloguj się</Link>
            </Button>
          )}
        </div>
      </AuthLayout>
    );
  }

  if (preview.isPending || session.isPending) {
    return (
      <AuthLayout title="Zaproszenie">
        <div className="flex justify-center py-6" role="status" aria-label="Wczytywanie">
          <Spinner size={20} className="text-fg-muted" />
        </div>
      </AuthLayout>
    );
  }

  if (preview.isError) {
    return (
      <AuthLayout title="Zaproszenie" footer={backLink}>
        <ErrorState
          compact
          offline={preview.error instanceof NetworkError}
          title={errorMessage(preview.error)}
          onRetry={() => void preview.refetch()}
          retrying={preview.isFetching}
        />
      </AuthLayout>
    );
  }

  const inv = preview.data;
  const roleLabel = ROLE_LABELS[inv.role];
  const who = inv.inviterName ? `${inv.inviterName} zaprasza Cię` : 'Zaproszenie';
  // portal klienta (17.10.2026): zgłoszenia i ich status zamiast „dołącz do zespołu”
  const portalClient = inv.portalClientName;
  const lead = portalClient
    ? `${who} do portalu zgłoszeń ${inv.workspace.name} dla ${portalClient}. Zgłosisz tu, czego potrzebujesz, i zobaczysz status oraz odpowiedzi.`
    : `${who} do ${inv.workspace.name} jako ${roleLabel}.`;
  const joinTitle = portalClient ? `Portal zgłoszeń ${inv.workspace.name}` : `Dołącz do ${inv.workspace.name}`;
  const joinLabel = portalClient ? 'Przejdź do portalu' : `Dołącz do ${inv.workspace.name}`;
  const current = session.data?.authenticated ? session.data : null;
  const goToLogin = () =>
    navigate(`/login?next=${encodeURIComponent(TEAM_WEB_PATHS.invitation)}`, { state: { email: inv.email } });

  // ── Konto z tym adresem już istnieje ──
  if (inv.accountExists) {
    const sameUser = current?.user.email.toLowerCase() === inv.email.toLowerCase();
    return (
      <AuthLayout title={joinTitle} description={lead} footer={backLink}>
        <div className="flex flex-col gap-4">
          {!current && (
            <>
              <p className="text-body-s text-fg-secondary">
                Masz już konto <strong className="font-medium text-fg">{inv.email}</strong>. Zaloguj się, a potem
                dołączysz jednym kliknięciem.
              </p>
              <Button variant="primary" size="l" fullWidth icon={LogIn} onClick={goToLogin}>
                Zaloguj się
              </Button>
            </>
          )}
          {current && sameUser && (
            <>
              <p className="text-body-s text-fg-secondary">
                Jesteś zalogowany jako <strong className="font-medium text-fg">{inv.email}</strong>.
              </p>
              {acceptError && <FormMessage>{errorMessage(acceptError)}</FormMessage>}
              <Button
                variant="primary"
                size="l"
                fullWidth
                icon={UserPlus}
                loading={accept.isPending}
                onClick={() => accept.mutate({ token })}
              >
                {joinLabel}
              </Button>
            </>
          )}
          {current && !sameUser && (
            <>
              <FormMessage>
                Jesteś zalogowany jako {current.user.email}. To zaproszenie jest dla {inv.email}.
              </FormMessage>
              <Button
                variant="secondary"
                size="l"
                fullWidth
                loading={logout.isPending}
                onClick={() =>
                  void logout
                    .mutateAsync()
                    .catch(() => undefined)
                    .finally(goToLogin)
                }
              >
                Wyloguj się i zaloguj na właściwe konto
              </Button>
            </>
          )}
        </div>
      </AuthLayout>
    );
  }

  // ── Nowe konto ──
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);
    const next: FieldErrors = {};
    if (name.trim().length < 2) next.name = TEAM_MESSAGES.nameRequired;
    if (problem === 'Hasła się różnią') next.confirm = problem;
    else if (problem) next.password = problem;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      await accept.mutateAsync({ token, name: name.trim(), password });
    } catch (err) {
      const mapped: FieldErrors = {};
      for (const issue of validationIssues(err)) {
        if ((issue.path === 'name' || issue.path === 'password') && !mapped[issue.path]) {
          mapped[issue.path] = issue.message;
        }
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
        return;
      }
      if (appErrorCode(err) === 'UNAUTHENTICATED') {
        // konto powstało w międzyczasie: podgląd pokaże logowanie
        void preview.refetch();
        return;
      }
      if (!invitationReason(err)) setFormError(errorMessage(err));
    }
  };

  return (
    <AuthLayout title={joinTitle} description={lead} footer={backLink}>
      <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Załóż konto">
        {current && (
          <ContextBanner tone="info">
            Jesteś zalogowany jako {current.user.email}. Po dołączeniu zalogujemy Cię na nowe konto.
          </ContextBanner>
        )}
        <Field label="Email" hint="Adres z zaproszenia. Będzie Twoim loginem.">
          <Input type="email" value={inv.email} readOnly disabled autoComplete="username" />
        </Field>
        <Field label="Imię i nazwisko" error={errors.name}>
          <Input autoComplete="name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Hasło" error={submitted ? errors.password : null}>
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-describedby="invitation-password-checks"
          />
        </Field>
        <Field label="Powtórz hasło" error={submitted ? errors.confirm : null}>
          <PasswordInput autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <PasswordChecklist id="invitation-password-checks" checks={checks} />
        {formError && <FormMessage>{formError}</FormMessage>}
        <Button type="submit" variant="primary" size="l" fullWidth loading={accept.isPending} className="mt-1">
          {portalClient ? 'Załóż konto' : 'Załóż konto i dołącz'}
        </Button>
      </form>
    </AuthLayout>
  );
}
